import { useState, useEffect } from 'react';

const getScrollTop = (target) => {
  if (!target || target === window) {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }
  return target.scrollTop || 0;
};

const resolveScrollTarget = (target) => {
  if (typeof window === 'undefined') return null;
  if (!target) return window;
  if (typeof target === 'string') return document.querySelector(target) || window;
  if (target.current) return target.current || window;
  return target;
};

export function useScrollDirection(options = {}) {
  const config = typeof options === 'string' ? { target: options } : options;
  const {
    target = null,
    threshold = 6,
    topOffset = 20,
    refreshKey = '',
  } = config || {};

  const [scrollDirection, setScrollDirection] = useState('up');
  const [isTop, setIsTop] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const scrollTarget = resolveScrollTarget(target);
    let lastScrollY = getScrollTop(scrollTarget);
    let ticking = false;

    setIsTop(lastScrollY < topOffset);

    const updateScrollDirection = () => {
      const scrollY = getScrollTop(scrollTarget);
      const delta = scrollY - lastScrollY;

      if (Math.abs(delta) >= threshold) {
        const direction = delta > 0 ? 'down' : 'up';
        setScrollDirection((current) => (current === direction ? current : direction));
        lastScrollY = Math.max(scrollY, 0);
      }

      setIsTop(scrollY < topOffset);
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateScrollDirection();
        ticking = false;
      });
    };

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [target, threshold, topOffset, refreshKey]);

  return { scrollDirection, isTop };
}
